/**
 * Platform Broker Constants
 *
 * The platform acts as an intermediary between buyers and sellers.
 * All communication is routed through the Platform Admin user.
 */

import { isIdentityRevealed } from "@/lib/deals/types";

/**
 * Platform Admin User ID - a reserved system user in the database
 * This user represents "Ökopunktemarkt" in conversations and deals
 */
export const PLATFORM_ADMIN_ID =
  process.env.PLATFORM_ADMIN_USER_ID || "00000000-0000-0000-0000-000000000001";

/**
 * Display information for the Platform
 */
export const PLATFORM_DISPLAY = {
  name: "Ökopunktemarkt Support",
  shortName: "Ökopunktemarkt",
  company: "Ökopunktemarkt",
  email: "support@oekopunktemarkt.de",
  avatar: "/oekopunktemarkt-avatar.png",
} as const;

/**
 * Anonymized display names for users
 * Used when displaying counterparty information
 */
export const ANONYMOUS_SELLER = "Verifizierter Anbieter";
export const ANONYMOUS_BUYER = "Geprüfter Interessent";

/**
 * Platform USPs (Unique Selling Points)
 * Used in ContactModal and other places
 */
export const PLATFORM_USPS = {
  legal: {
    icon: "Shield",
    title: "Rechtssichere Verträge",
    description: "Alle Verträge werden rechtlich geprüft",
  },
  anonymous: {
    icon: "Lock",
    title: "Anonymität garantiert",
    description: "Ihre Identität bleibt bis zum Vertragsabschluss geschützt",
  },
  advisory: {
    icon: "Users",
    title: "Persönliche Beratung",
    description: "Experten unterstützen Sie bei jedem Schritt",
  },
  escrow: {
    icon: "FileCheck",
    title: "Treuhand-Abwicklung",
    description: "Sichere Zahlungsabwicklung über Treuhandkonto",
  },
} as const;

/**
 * Vermittlungsprovision (AGB-Provision §6.1)
 * — applied to Gesamtpreis inkl. USt
 * — Mindestprovision is **netto**; USt wird zusätzlich berechnet
 *
 * Values are env-overridable for ops tuning of the funnel hint.
 * The contractually binding numbers live in the AGB-Provision MDX —
 * update both together if you change the legal rate.
 */
const parseEnvNumber = (raw: string | undefined, fallback: number): number => {
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const COMMISSION_RATE_PERCENT = parseEnvNumber(
  process.env.NEXT_PUBLIC_COMMISSION_RATE_PERCENT,
  6
);
export const COMMISSION_RATE = COMMISSION_RATE_PERCENT / 100;
export const MIN_PROVISION_EUR_NET = parseEnvNumber(
  process.env.NEXT_PUBLIC_MIN_PROVISION_EUR_NET,
  1200
);

/**
 * Check if a user is an admin by querying the database
 * Use this in API routes and server-side code
 *
 * @param supabase - Supabase client instance (server or client)
 * @param userId - User ID to check
 * @returns true if user has is_admin = true in database
 */
export async function checkIsAdmin(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts both server and browser Supabase client types
  supabase: any,
  userId: string | null | undefined
): Promise<boolean> {
  if (!userId) return false;

  try {
    const { data } = await supabase.from("users").select("is_admin").eq("id", userId).maybeSingle();

    return data?.is_admin === true;
  } catch {
    return false;
  }
}

/**
 * Get anonymized display name based on user role
 */
export function getAnonymousName(role: "seller" | "buyer"): string {
  return role === "seller" ? ANONYMOUS_SELLER : ANONYMOUS_BUYER;
}

/**
 * Strip user identification from an offering for public display
 * In broker model, we don't expose who owns the offering
 */
export function anonymizeOffering<T extends { user_id?: string }>(
  offering: T,
  requestingUserId?: string | null
): Omit<T, "user_id" | "posthog_context"> & {
  is_owner: boolean;
  seller_verified: boolean;
} {
  // Strip user_id AND the internal analytics context (funnel session +
  // acquisition attribution) — neither belongs in a public/anonymized offering.
  const {
    user_id,
    posthog_context: _posthogContext,
    ...rest
  } = offering as T & {
    posthog_context?: unknown;
  };

  return {
    ...rest,
    is_owner: requestingUserId ? user_id === requestingUserId : false,
    seller_verified: true, // All offerings on platform are verified
  };
}

/**
 * Strip user identification from multiple offerings
 */
export function anonymizeOfferings<T extends { user_id?: string }>(
  offerings: T[],
  requestingUserId?: string | null
): Array<
  Omit<T, "user_id" | "posthog_context"> & {
    is_owner: boolean;
    seller_verified: boolean;
  }
> {
  return offerings.map((o) => anonymizeOffering(o, requestingUserId));
}

/**
 * Anonymize deal for display - hide counterparty info
 * In broker model, users see "Ökopunktemarkt" as their partner
 */
export interface AnonymizedDealRelations {
  seller?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  };
  buyer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  };
}

export function anonymizeDeal<
  T extends AnonymizedDealRelations & {
    seller_id?: string | null;
    buyer_id?: string | null;
    status?: string;
  },
>(
  deal: T,
  requestingUserId: string
): Omit<T, "visibility_changed_at" | "visibility_changed_by"> & {
  partner_display: { name: string; company: string };
  user_role: "seller" | "buyer";
} {
  // Who changed the deal's visibility, and when, is staff's own record (ARCH-026), like the
  // visibility_changed events the parties' history leaves out: a party never reads it.
  const {
    visibility_changed_at: _visibilityChangedAt,
    visibility_changed_by: _visibilityChangedBy,
    ...rest
  } = deal as T & { visibility_changed_at?: unknown; visibility_changed_by?: unknown };
  const userRole = deal.seller_id === requestingUserId ? "seller" : "buyer";

  // After contract stage (awaiting_signatures+), reveal real counterparty identity
  const isRevealed = isIdentityRevealed(deal.status ?? "");

  // Replace counterparty info with Platform display (unless revealed)
  const anonymizedSeller =
    userRole === "seller" || isRevealed
      ? deal.seller
      : {
          id: PLATFORM_ADMIN_ID,
          first_name: PLATFORM_DISPLAY.shortName,
          last_name: null,
          company_name: PLATFORM_DISPLAY.company,
        };

  const anonymizedBuyer =
    userRole === "buyer" || isRevealed
      ? deal.buyer
      : {
          id: PLATFORM_ADMIN_ID,
          first_name: PLATFORM_DISPLAY.shortName,
          last_name: null,
          company_name: PLATFORM_DISPLAY.company,
        };

  const counterparty = userRole === "seller" ? deal.buyer : deal.seller;
  const partnerDisplay =
    isRevealed && counterparty
      ? {
          name:
            [counterparty.first_name, counterparty.last_name].filter(Boolean).join(" ") ||
            PLATFORM_DISPLAY.name,
          company: counterparty.company_name || PLATFORM_DISPLAY.company,
        }
      : {
          name: PLATFORM_DISPLAY.name,
          company: PLATFORM_DISPLAY.company,
        };

  return {
    ...rest,
    seller: anonymizedSeller,
    buyer: anonymizedBuyer,
    partner_display: partnerDisplay,
    user_role: userRole,
  };
}

/**
 * Anonymize multiple deals
 */
export function anonymizeDeals<
  T extends AnonymizedDealRelations & { seller_id?: string | null; buyer_id?: string | null },
>(
  deals: T[],
  requestingUserId: string
): Array<
  Omit<T, "visibility_changed_at" | "visibility_changed_by"> & {
    partner_display: { name: string; company: string };
    user_role: "seller" | "buyer";
  }
> {
  return deals.map((d) => anonymizeDeal(d, requestingUserId));
}
